import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { supabase } from './supabase';
import { RefreshCw, ExternalLink, Send, Trash2, ShoppingCart, CheckCircle2, Save, Settings as SettingsIcon, Phone, MousePointerClick, Loader2 } from 'lucide-react';
import './AbandonedCarts.css';

const fmtARS = (n: number | null) =>
  (n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface Cart {
  checkout_id: number;
  token: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  total: number | null;
  currency: string | null;
  products: { name: string; price?: string; quantity?: number; image?: string | null }[] | null;
  recovery_url: string | null;
  tn_created_at: string | null;
  completed_at: string | null;
  status: string;
  first_msg_at: string | null;
  second_msg_at: string | null;
  recovered_order_id: number | null;
  recovered_at: string | null;
  click_count: number | null;
  first_click_at: string | null;
  last_click_at: string | null;
  skipped_reason: string | null;
  variant: string | null;
  third_msg_at: string | null;
}

const STATUS_META: Record<string, { label: string; cls: string }> = {
  pendiente: { label: 'Por enviar', cls: 'st-pend' },
  enviado: { label: 'Mensaje enviado', cls: 'st-sent' },
  insistencia: { label: '2º mensaje enviado', cls: 'st-insist' },
  ultimo: { label: 'Último aviso (3º)', cls: 'st-insist' },
  recuperado: { label: 'Recuperado', cls: 'st-recov' },
  descartado: { label: 'Descartado', cls: 'st-disc' },
  sin_telefono: { label: 'Sin teléfono', cls: 'st-nophone' },
};

const ACTIVE_STATUSES = ['pendiente', 'enviado', 'insistencia', 'ultimo'];

// Estado de aprobación de cada template en Meta
const TPL_ST: Record<string, { t: string; c: string }> = {
  no_enviado: { t: 'No enviado', c: 'st-nophone' },
  pending: { t: 'En revisión de Meta', c: 'st-sent' },
  approved: { t: 'Aprobado ✓', c: 'st-recov' },
  rejected: { t: 'Rechazado', c: 'st-pend' },
};

const FILTERS = [
  { key: 'todos', label: 'Todos' },
  { key: 'activos', label: 'Activos' },
  { key: 'pendiente', label: 'Por enviar' },
  { key: 'enviado', label: 'Enviados' },
  { key: 'insistencia', label: 'Insistencia' },
  { key: 'recuperado', label: 'Recuperados' },
  { key: 'descartado', label: 'Descartados' },
];

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} d`;
}

export default function AbandonedCarts({ showSnackbar }: { showSnackbar?: (msg: string, t: 'success' | 'error') => void }) {
  const [carts, setCarts] = useState<Cart[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState('todos');
  const [busyId, setBusyId] = useState<number | null>(null);
  // Menú de "elegir variante": va con position:fixed porque el wrapper de la tabla
  // tiene overflow-x:auto y recortaría un popover absoluto.
  const [menuFor, setMenuFor] = useState<{ id: number; top: number; right: number } | null>(null);

  // Config (cost_settings)
  const [cfg, setCfg] = useState<{ enabled: boolean; delay1: number; delay2: number; delay3: number; quietStart: number; quietEnd: number; cooldown: number; abEnabled: boolean; bPct: number; body1: string; body2: string; tpl1: string; tpl2: string; tpl3: string; tpl4: string }>({ enabled: false, delay1: 30, delay2: 1440, delay3: 4320, quietStart: 9, quietEnd: 21, cooldown: 30, abEnabled: false, bPct: 50, body1: '', body2: '', tpl1: 'no_enviado', tpl2: 'no_enviado', tpl3: 'no_enviado', tpl4: 'no_enviado' });
  const [savingCfg, setSavingCfg] = useState(false);
  const [showCfg, setShowCfg] = useState(false);
  const [submitting, setSubmitting] = useState<1 | 2 | null>(null);

  const snack = useCallback((m: string, t: 'success' | 'error' = 'success') => { showSnackbar?.(m, t); }, [showSnackbar]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('abandoned_carts').select('*').order('tn_created_at', { ascending: false }).limit(500);
    setCarts((data as Cart[]) || []);
    const { data: cs } = await supabase.from('cost_settings').select('abandoned_cart_enabled, abandoned_cart_delay1_min, abandoned_cart_delay2_min, abandoned_cart_delay3_min, abandoned_cart_quiet_start, abandoned_cart_quiet_end, abandoned_cart_cooldown_days, abandoned_cart_ab_enabled, abandoned_cart_variant_b_pct, abandoned_cart_body_1, abandoned_cart_body_2, abandoned_cart_tpl1_status, abandoned_cart_tpl2_status, abandoned_cart_tpl3_status, abandoned_cart_tpl4_status').eq('id', 1).maybeSingle();
    if (cs) setCfg({ enabled: !!cs.abandoned_cart_enabled, delay1: cs.abandoned_cart_delay1_min ?? 30, delay2: cs.abandoned_cart_delay2_min ?? 1440, delay3: cs.abandoned_cart_delay3_min ?? 4320, quietStart: cs.abandoned_cart_quiet_start ?? 9, quietEnd: cs.abandoned_cart_quiet_end ?? 21, cooldown: cs.abandoned_cart_cooldown_days ?? 30, abEnabled: !!cs.abandoned_cart_ab_enabled, bPct: cs.abandoned_cart_variant_b_pct ?? 50, body1: cs.abandoned_cart_body_1 || '', body2: cs.abandoned_cart_body_2 || '', tpl1: cs.abandoned_cart_tpl1_status || 'no_enviado', tpl2: cs.abandoned_cart_tpl2_status || 'no_enviado', tpl3: cs.abandoned_cart_tpl3_status || 'no_enviado', tpl4: cs.abandoned_cart_tpl4_status || 'no_enviado' });
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-abandoned-carts', { body: {} });
      if (error) throw error;
      snack(`Sincronizado: ${data?.upserted ?? 0} carritos, ${data?.recovered ?? 0} recuperados`);
      await load();
    } catch (e: any) { snack('Error al sincronizar: ' + (e?.message || e), 'error'); }
    setSyncing(false);
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    const { error } = await supabase.from('cost_settings').update({
      abandoned_cart_enabled: cfg.enabled,
      abandoned_cart_delay1_min: cfg.delay1,
      abandoned_cart_delay2_min: cfg.delay2,
      abandoned_cart_quiet_start: cfg.quietStart,
      abandoned_cart_quiet_end: cfg.quietEnd,
      abandoned_cart_cooldown_days: cfg.cooldown,
      abandoned_cart_delay3_min: cfg.delay3,
      abandoned_cart_ab_enabled: cfg.abEnabled,
      abandoned_cart_variant_b_pct: cfg.bPct,
      abandoned_cart_body_1: cfg.body1,
      abandoned_cart_body_2: cfg.body2,
    }).eq('id', 1);
    setSavingCfg(false);
    snack(error ? 'Error al guardar' : 'Configuración guardada', error ? 'error' : 'success');
  };

  // Manda el template a Meta para aprobación (el usuario lo dispara desde acá).
  const submitToMeta = async (n: 1 | 2) => {
    const body = n === 1 ? cfg.body1 : cfg.body2;
    const name = n === 1 ? 'carrito_abandonado_v2' : 'carrito_abandonado_insistencia';
    const placeholders = (body.match(/\{\{\d+\}\}/g) || []).length;
    // tpl1 → 3 variables (nombre, producto, link). tpl2 → 4 (nombre, producto, código, link).
    const need = n === 1 ? 3 : 4;
    if (placeholders !== need) {
      snack(n === 1
        ? 'El 1er mensaje debe tener {{1}} nombre, {{2}} producto y {{3}} link (3 variables).'
        : 'La insistencia debe tener {{1}} nombre, {{2}} producto, {{3}} código y {{4}} link (4 variables).', 'error');
      return;
    }
    // Meta rechaza plantillas que empiezan o terminan con una variable.
    const trimmed = body.trim();
    if (/^\{\{\d+\}\}/.test(trimmed) || /\{\{\d+\}\}$/.test(trimmed)) {
      snack('Meta no acepta plantillas que empiecen o terminen con una variable. Agregá texto antes de {{1}} (ej. "Hola {{1}}") y/o después de la última variable.', 'error');
      return;
    }
    const examples = n === 1
      ? ['Sofía', 'Lámpara Luna', 'https://krilic.com.ar/checkout']
      : ['Sofía', 'Lámpara Luna', 'VOLVE-3F2A-SOFIA', 'https://krilic.com.ar/checkout'];
    setSubmitting(n);
    try {
      // Guardamos el texto antes de mandarlo (así queda igual al que va a Meta).
      await supabase.from('cost_settings').update(n === 1 ? { abandoned_cart_body_1: body } : { abandoned_cart_body_2: body }).eq('id', 1);
      const { data, error } = await supabase.functions.invoke('submit-meta-template', {
        body: { name, body, language: 'es_AR', category: 'MARKETING', examples },
      });
      if (error) throw error;
      if (data?.success) {
        const status = (data.status || 'PENDING').toLowerCase();
        await supabase.from('cost_settings').update(n === 1 ? { abandoned_cart_tpl1_status: status } : { abandoned_cart_tpl2_status: status }).eq('id', 1);
        setCfg(prev => ({ ...prev, [n === 1 ? 'tpl1' : 'tpl2']: status } as any));
        snack(`Template "${name}" enviado a Meta (estado: ${status}). Te avisan por mail cuando lo aprueben.`);
      } else {
        snack('Meta rechazó el envío: ' + (data?.error || 'error desconocido'), 'error');
      }
    } catch (e: any) { snack('Error al enviar a Meta: ' + (e?.message || e), 'error'); }
    setSubmitting(null);
  };

  const discard = async (c: Cart) => {
    setBusyId(c.checkout_id);
    await supabase.from('abandoned_carts').update({ status: 'descartado' }).eq('checkout_id', c.checkout_id);
    setCarts(prev => prev.map(x => x.checkout_id === c.checkout_id ? { ...x, status: 'descartado' } : x));
    setBusyId(null);
  };

  // Paso que corresponde mandar según lo ya enviado (la B tiene un 3er mensaje).
  const nextKind = (c: Cart): 'first' | 'second' | 'third' =>
    !c.first_msg_at ? 'first' : !c.second_msg_at ? 'second' : 'third';

  const phoneTail = (p: string | null) => (p || '').replace(/\D/g, '').slice(-10);

  const sendNow = async (c: Cart, kind: 'first' | 'second' | 'third', variant?: 'A' | 'B') => {
    setMenuFor(null);
    // El cooldown anti-duplicados corre solo en el envío automático. Acá no bloqueamos
    // (si apretás el botón es a propósito) pero avisamos: el mismo teléfono puede tener
    // varios carritos y cada uno mandaría su propio mensaje.
    if (kind === 'first') {
      const t = phoneTail(c.contact_phone);
      const otro = t ? carts.find(x => x.checkout_id !== c.checkout_id && x.first_msg_at && phoneTail(x.contact_phone) === t) : null;
      if (otro) {
        const cuando = new Date(otro.first_msg_at!).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
        if (!window.confirm(`A este teléfono (${c.contact_phone}) ya le mandamos un mensaje el ${cuando} por otro carrito (${otro.contact_name || 's/n'}).\n\n¿Mandar igual?`)) return;
      }
    }
    setBusyId(c.checkout_id);
    try {
      const { data, error } = await supabase.functions.invoke('process-abandoned-carts', { body: { checkout_id: c.checkout_id, kind, variant, force: true } });
      if (error) throw error;
      // Refrescamos SOLO esa fila: un load() completo pone la pantalla en "cargando"
      // y te hace perder el scroll y el filtro.
      if (data?.ok) {
        snack('Mensaje enviado');
        const { data: fresh } = await supabase.from('abandoned_carts').select('*').eq('checkout_id', c.checkout_id).maybeSingle();
        if (fresh) setCarts(prev => prev.map(x => x.checkout_id === c.checkout_id ? (fresh as Cart) : x));
      }
      else snack('No se pudo enviar: ' + (data?.error || 'revisá el template de WhatsApp'), 'error');
    } catch (e: any) { snack('Error: ' + (e?.message || e), 'error'); }
    setBusyId(null);
  };

  const filtered = useMemo(() => {
    if (filter === 'todos') return carts;
    if (filter === 'activos') return carts.filter(c => ACTIVE_STATUSES.includes(c.status));
    return carts.filter(c => c.status === filter);
  }, [carts, filter]);

  const stats = useMemo(() => {
    const pend = carts.filter(c => ACTIVE_STATUSES.includes(c.status));
    const recov = carts.filter(c => c.status === 'recuperado');
    const pendTotal = pend.reduce((s, c) => s + (c.total || 0), 0);
    const recovTotal = recov.reduce((s, c) => s + (c.total || 0), 0);
    const denom = pend.length + recov.length;
    const rate = denom ? Math.round((recov.length / denom) * 100) : 0;
    // Clicks: sobre los que recibieron al menos un mensaje.
    const messaged = carts.filter(c => c.first_msg_at || c.second_msg_at);
    const clicked = carts.filter(c => (c.click_count || 0) > 0);
    const clickRate = messaged.length ? Math.round((clicked.length / messaged.length) * 100) : 0;

    // Recupero POR CLICK: la atribución más dura — clickeó NUESTRO link y después compró.
    // Un recuperado sin click (fue solo a la web, o habría comprado igual) NO cuenta acá.
    const byClick = (c: Cart) => c.status === 'recuperado' && (c.click_count || 0) > 0
      && !!c.first_click_at && (!c.recovered_at || new Date(c.first_click_at) <= new Date(c.recovered_at));
    const recovByClick = carts.filter(byClick);
    const recovByClickTotal = recovByClick.reduce((s, c) => s + (c.total || 0), 0);

    // A/B: sólo carritos ya contactados (los que tienen variante asignada).
    const ab = ['A', 'B'].map(v => {
      const arr = carts.filter(c => c.variant === v);
      const rec = arr.filter(c => c.status === 'recuperado');
      const clk = arr.filter(c => (c.click_count || 0) > 0);
      const recClick = arr.filter(byClick);
      return {
        variant: v, n: arr.length, recN: rec.length,
        recRate: arr.length ? Math.round((rec.length / arr.length) * 100) : 0,
        clickRate: arr.length ? Math.round((clk.length / arr.length) * 100) : 0,
        recTotal: rec.reduce((s, c) => s + (c.total || 0), 0),
        recClickN: recClick.length,
      };
    });

    return { pendN: pend.length, pendTotal, recovN: recov.length, recovTotal, rate, clickedN: clicked.length, messagedN: messaged.length, clickRate, recovByClickN: recovByClick.length, recovByClickTotal, ab };
  }, [carts]);

  const prodSummary = (c: Cart) => {
    const ps = c.products || [];
    if (!ps.length) return '—';
    const names = ps.map(p => (p.name || '').replace(/\s*-\s*.*$/, '').trim());
    const first = names[0];
    return names.length > 1 ? `${first} +${names.length - 1}` : first;
  };

  return (
    <div className="ac-wrap">
      <div className="ac-header">
        <div className="ac-title"><ShoppingCart size={20} /> <h2>Carritos abandonados</h2></div>
        <div className="ac-actions">
          <button className="ac-btn ghost" onClick={() => setShowCfg(v => !v)}><SettingsIcon size={15} /> Configurar</button>
          <button className="ac-btn" onClick={sync} disabled={syncing}><RefreshCw size={15} className={syncing ? 'spin' : ''} /> {syncing ? 'Sincronizando…' : 'Sincronizar'}</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="ac-kpis">
        <div className="ac-kpi">
          <span className="ac-kpi-label">Activos (sin recuperar)</span>
          <span className="ac-kpi-val">{stats.pendN}</span>
          <span className="ac-kpi-sub">${fmtARS(stats.pendTotal)} en juego</span>
        </div>
        <div className="ac-kpi recov">
          <span className="ac-kpi-label">Recuperados</span>
          <span className="ac-kpi-val">{stats.recovN}</span>
          <span className="ac-kpi-sub">${fmtARS(stats.recovTotal)} recuperados</span>
        </div>
        <div className="ac-kpi">
          <span className="ac-kpi-label">Tasa de recuperación</span>
          <span className="ac-kpi-val">{stats.rate}%</span>
          <span className="ac-kpi-sub">de los carritos con teléfono</span>
        </div>
        <div className="ac-kpi click">
          <span className="ac-kpi-label">Clickearon el link</span>
          <span className="ac-kpi-val">{stats.clickedN}</span>
          <span className="ac-kpi-sub">{stats.clickRate}% de los {stats.messagedN} contactados</span>
        </div>
        <div className="ac-kpi recov" title="Atribución dura: clickeó nuestro link de recuperación Y después compró. Los recuperados sin click (orgánicos o que fueron directo a la web) no cuentan acá.">
          <span className="ac-kpi-label">Recuperados por click</span>
          <span className="ac-kpi-val">{stats.recovByClickN}</span>
          <span className="ac-kpi-sub">${fmtARS(stats.recovByClickTotal)} atribuible al link</span>
        </div>
      </div>

      {/* Comparativa A/B (aparece recién cuando hay carritos con variante asignada) */}
      {stats.ab.some(v => v.n > 0) && (
        <div className="ac-ab-compare">
          <div className="ac-ab-title">A/B — estrategia de recupero</div>
          <table className="ac-ab-table">
            <thead>
              <tr><th>Variante</th><th className="r">Carritos</th><th className="r">Clickearon</th><th className="r">Recuperados</th><th className="r" title="Clickeó el link y después compró">Por click</th><th className="r">Tasa</th><th className="r">$ recuperado</th></tr>
            </thead>
            <tbody>
              {stats.ab.map(v => (
                <tr key={v.variant}>
                  <td><span className={`ac-variant v-${v.variant}`}>{v.variant}</span> {v.variant === 'A' ? 'cupón a las 24h' : 'objeciones primero, cupón a las 72h'}</td>
                  <td className="r">{v.n}</td>
                  <td className="r">{v.clickRate}%</td>
                  <td className="r">{v.recN}</td>
                  <td className="r">{v.recClickN}</td>
                  <td className="r"><b>{v.recRate}%</b></td>
                  <td className="r">${fmtARS(v.recTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="ac-cfg-note">Con el volumen actual (~38 carritos por semana) esto es una señal direccional, no un test estadísticamente concluyente: hacen falta varios meses para que la diferencia sea confiable.</p>
        </div>
      )}

      {/* Config panel */}
      {showCfg && (
        <div className="ac-cfg">
          <label className="ac-switch">
            <input type="checkbox" checked={cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} />
            <span>Envío automático de WhatsApp {cfg.enabled ? 'activado' : 'desactivado'}</span>
          </label>
          <div className="ac-cfg-row">
            <label>1er mensaje a los <input type="number" min={5} value={cfg.delay1} onChange={e => setCfg({ ...cfg, delay1: +e.target.value })} /> min de abandonar</label>
            <label>2ª insistencia a los <input type="number" min={10} value={cfg.delay2} onChange={e => setCfg({ ...cfg, delay2: +e.target.value })} /> min de abandonar</label>
          </div>
          <div className="ac-cfg-row">
            <label>Enviar solo entre las <input type="number" min={0} max={23} value={cfg.quietStart} onChange={e => setCfg({ ...cfg, quietStart: +e.target.value })} /> y las <input type="number" min={1} max={24} value={cfg.quietEnd} onChange={e => setCfg({ ...cfg, quietEnd: +e.target.value })} /> hs</label>
            <label>No repetir al mismo teléfono por <input type="number" min={0} value={cfg.cooldown} onChange={e => setCfg({ ...cfg, cooldown: +e.target.value })} /> días</label>
          </div>
          <p className="ac-cfg-note">Los carritos abandonados de madrugada no se pierden: esperan a la primera corrida dentro de la ventana horaria. El cooldown evita que un cliente con dos carritos abiertos reciba cuatro mensajes.</p>

          {/* A/B test de estrategia */}
          <div className="ac-ab-box">
            <label className="ac-switch">
              <input type="checkbox" checked={cfg.abEnabled} onChange={e => setCfg({ ...cfg, abEnabled: e.target.checked })} />
              <span>A/B test de estrategia {cfg.abEnabled ? 'activo' : 'apagado'}</span>
            </label>
            <div className="ac-cfg-row">
              <label>Mandar a la variante B el <input type="number" min={0} max={100} value={cfg.bPct} onChange={e => setCfg({ ...cfg, bPct: +e.target.value })} /> % de los carritos</label>
              <label>3er mensaje (solo B) a los <input type="number" min={60} value={cfg.delay3} onChange={e => setCfg({ ...cfg, delay3: +e.target.value })} /> min de abandonar</label>
            </div>
            <div className="ac-ab-legend">
              <div><b>A</b> — la actual: recordatorio a los {cfg.delay1} min y cupón 5% a las {Math.round(cfg.delay2 / 60)} h. <span className={`ac-badge ${TPL_ST[cfg.tpl1]?.c || ''}`}>{TPL_ST[cfg.tpl1]?.t || cfg.tpl1}</span></div>
              <div><b>B</b> — objeciones primero: recordatorio con foto y botón, a las {Math.round(cfg.delay2 / 60)} h medios de pago y envío (sin cupón), y el cupón recién a las {Math.round(cfg.delay3 / 60)} h. <span className={`ac-badge ${TPL_ST[cfg.tpl3]?.c || ''}`}>v3: {TPL_ST[cfg.tpl3]?.t || cfg.tpl3}</span> <span className={`ac-badge ${TPL_ST[cfg.tpl4]?.c || ''}`}>ayuda: {TPL_ST[cfg.tpl4]?.t || cfg.tpl4}</span></div>
            </div>
            <p className="ac-cfg-note">Prendelo recién cuando Meta apruebe los dos templates de la B. Un carrito sin foto de producto cae automáticamente a la variante A.</p>
          </div>

          {/* Editor de templates */}
          <div className="ac-tpl-editor">
            <p className="ac-cfg-note">Editá los textos y mandalos a Meta para aprobación. <b>1er mensaje</b> (3 variables): <code>{'{{1}}'}</code> nombre · <code>{'{{2}}'}</code> producto · <code>{'{{3}}'}</code> link. <b>2ª insistencia</b> (4 variables): <code>{'{{1}}'}</code> nombre · <code>{'{{2}}'}</code> producto · <code>{'{{3}}'}</code> código de descuento · <code>{'{{4}}'}</code> link.</p>
            {[1, 2].map(n => {
              const body = n === 1 ? cfg.body1 : cfg.body2;
              const st = n === 1 ? cfg.tpl1 : cfg.tpl2;
              const s = TPL_ST[st] || { t: st, c: '' };
              return (
                <div className="ac-tpl-block" key={n}>
                  <div className="ac-tpl-head">
                    <b>{n === 1 ? '1er mensaje (recordatorio)' : '2ª insistencia (con descuento)'}</b>
                    <span className={`ac-badge ${s.c}`}>{s.t}</span>
                  </div>
                  <textarea className="ac-tpl-ta" rows={4} value={body}
                    onChange={e => setCfg(prev => ({ ...prev, [n === 1 ? 'body1' : 'body2']: e.target.value } as any))} />
                  <div className="ac-tpl-actions">
                    <button className="ac-btn ghost" onClick={saveCfg} disabled={savingCfg}><Save size={14} /> Guardar texto</button>
                    <button className="ac-btn" onClick={() => submitToMeta(n as 1 | 2)} disabled={submitting === n}>
                      <Send size={14} /> {submitting === n ? 'Enviando…' : 'Enviar a Meta'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="ac-cfg-note">⚠️ El envío automático solo funciona con los dos templates <b>Aprobados</b> por Meta y el switch de arriba activado. En la 2ª insistencia el sistema <b>crea un cupón único en Tiendanube</b> por carrito (5% OFF, un solo uso, vence en ~24hs) y lo manda como código {'{{3}}'} — no hace falta que lo crees a mano.</p>
          <div className="ac-cfg-row"><button className="ac-btn" onClick={saveCfg} disabled={savingCfg}><Save size={14} /> Guardar toda la configuración</button></div>
        </div>
      )}

      {/* Filtros */}
      <div className="ac-filters">
        {FILTERS.map(f => (
          <button key={f.key} className={`ac-chip ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}
            {f.key === 'activos' && <span className="ac-chip-n">{stats.pendN}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="ac-empty">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="ac-empty">No hay carritos en este filtro.</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th>Cliente</th><th>Productos</th><th className="r">Total</th><th>Abandonado</th><th>Estado</th><th>Mensajes</th><th>Click</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const sm = STATUS_META[c.status] || { label: c.status, cls: '' };
                const busy = busyId === c.checkout_id;
                return (
                  <tr key={c.checkout_id}>
                    <td>
                      <div className="ac-name">{c.contact_name || 'Sin nombre'}</div>
                      {c.contact_phone && <div className="ac-phone"><Phone size={11} /> {c.contact_phone}</div>}
                    </td>
                    <td className="ac-prod" title={(c.products || []).map(p => p.name).join(', ')}>{prodSummary(c)}</td>
                    <td className="r ac-total">${fmtARS(c.total)}</td>
                    <td className="ac-ago">{timeAgo(c.tn_created_at)}</td>
                    <td><span className={`ac-badge ${sm.cls}`} title={c.skipped_reason || undefined}>{sm.label}</span>
                      {c.skipped_reason && <div className="ac-skip-reason">{c.skipped_reason}</div>}
                    </td>
                    <td className="ac-msgs">
                      <span className={c.first_msg_at ? 'on' : ''} title={c.first_msg_at ? `1º: ${new Date(c.first_msg_at).toLocaleString('es-AR')}` : '1º sin enviar'}>1º</span>
                      <span className={c.second_msg_at ? 'on' : ''} title={c.second_msg_at ? `2º: ${new Date(c.second_msg_at).toLocaleString('es-AR')}` : '2º sin enviar'}>2º</span>
                      {c.variant === 'B' && <span className={c.third_msg_at ? 'on' : ''} title={c.third_msg_at ? `3º: ${new Date(c.third_msg_at).toLocaleString('es-AR')}` : '3º sin enviar'}>3º</span>}
                      {c.variant && <span className={`ac-variant v-${c.variant}`} title={c.variant === 'B' ? 'Variante B: objeciones primero, cupón a las 72h' : 'Variante A: cupón a las 24h'}>{c.variant}</span>}
                    </td>
                    <td className="ac-click">
                      {(c.click_count || 0) > 0 ? (
                        <span className="ac-click-on" title={`${c.click_count} click(s) · último: ${c.last_click_at ? new Date(c.last_click_at).toLocaleString('es-AR') : ''}`}>
                          <MousePointerClick size={13} /> {c.click_count}
                        </span>
                      ) : <span className="ac-click-off">—</span>}
                    </td>
                    <td className="ac-row-actions">
                      {c.recovery_url && <a className="ac-icon" href={c.recovery_url} target="_blank" rel="noreferrer" title="Ver carrito en la tienda"><ExternalLink size={15} /></a>}
                      {ACTIVE_STATUSES.includes(c.status) && c.contact_phone && (
                        <button className="ac-icon" disabled={busy}
                          title={c.variant ? `Enviar ${nextKind(c) === 'first' ? '1er' : nextKind(c) === 'second' ? '2º' : '3er'} mensaje (variante ${c.variant})` : 'Enviar mensaje ahora'}
                          onClick={e => {
                            if (c.variant) { sendNow(c, nextKind(c)); return; }
                            if (menuFor?.id === c.checkout_id) { setMenuFor(null); return; }
                            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setMenuFor({ id: c.checkout_id, top: r.bottom + 6, right: window.innerWidth - r.right });
                          }}>
                          {busy ? <Loader2 size={15} className="spin" /> : <Send size={15} />}
                        </button>
                      )}
                      {c.status !== 'recuperado' && c.status !== 'descartado' && (
                        <button className="ac-icon danger" disabled={busy} title="Descartar" onClick={() => discard(c)}><Trash2 size={15} /></button>
                      )}
                      {c.status === 'recuperado' && <CheckCircle2 size={16} className="ac-recov-ico" />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Elegir variante en el envío manual */}
      {menuFor && (() => {
        const c = carts.find(x => x.checkout_id === menuFor.id);
        if (!c) return null;
        const hasImg = (c.products || []).some(p => p.image);
        return (
          <>
            <div className="ac-send-backdrop" onClick={() => setMenuFor(null)} />
            <div className="ac-send-menu" style={{ top: menuFor.top, right: menuFor.right }}>
              <div className="ac-send-menu-t">¿Con qué versión?</div>
              <button onClick={() => sendNow(c, 'first', 'A')}>
                <span className="ac-variant v-A">A</span> Recordatorio simple
              </button>
              <button disabled={!hasImg} onClick={() => sendNow(c, 'first', 'B')}>
                <span className="ac-variant v-B">B</span> Con foto y botón{!hasImg && <em> — sin foto</em>}
              </button>
            </div>
          </>
        );
      })()}
    </div>
  );
}
